export function isEmpty(value) {
    // null or undefined
    if (value == null) return true;

    // boolean
    if (typeof value === "boolean") return value === false;

    // number
    if (typeof value === "number") return value === 0 || Number.isNaN(value);

    // string
    if (typeof value === "string") return value.trim().length === 0;

    // array
    if (Array.isArray(value)) return value.length === 0;

    // Map / Set
    if (value instanceof Map || value instanceof Set) return value.size === 0;

    // Date (invalid date)
    if (value instanceof Date) return Number.isNaN(value.getTime());

    // File / Blob
    if (value instanceof File || value instanceof Blob) return value.size === 0;

    // object
    if (typeof value === "object") return Object.keys(value).length === 0;

    return false;
}

export function formatPrice(price, decimalPlaces = 2) {
    if (!price && price !== 0) return "0.00";
    return parseFloat(price).toFixed(decimalPlaces);
}

// Shipping fee is stored at 2dp. For 0dp currencies (TWD) hide the decimals when the fee is a whole number.
export function getShippingFeeDecimalPlaces(fee, siteCurrencyDecimalPlaces, maxDecimalPlaces = 2) {
    if (Number(siteCurrencyDecimalPlaces) !== 0) return maxDecimalPlaces;

    const rounded = Number(parseFloat(fee || 0).toFixed(maxDecimalPlaces));
    return Number.isInteger(rounded) ? 0 : maxDecimalPlaces;
}

export function toFixedNumber(value, decimalPlaces = 6, preserveType = false) {
    const isStringInput = typeof value === "string";

    if (value == null || value === "" || isNaN(value)) {
        return preserveType && isStringInput ? "" : 0;
    }

    const num = Number(value);
    const factor = 10 ** decimalPlaces;

    const result = Math.round((num + Number.EPSILON) * factor) / factor;

    // return type handling
    if (preserveType && isStringInput) {
        return result.toString();
    }

    return result;
}

export function getSingleLineAddress(address, options = {}) {
    return this.renderAddress(address, "single", options);
}

export function getMultiLineAddress(address, options = {}) {
    return this.renderAddress(address, "multiple", options);
}

export function renderAddress(address, rule, options = {}) {
    const config = window.bo?.addressFormatMapper;

    if (this.isEmpty(config)) {
        return;
    }

    const fields = config.fields;
    const joins = config.join;
    const hideFields = options.hide_fields ?? [];

    const chunks = [];

    fields.forEach((field) => {
        if (hideFields.includes(field)) return;

        const value = address[field];
        if (!value) return;

        chunks.push({
            key: field,
            value: value.replace(/\s+/g, " ").replace(/,+$/, "").trim(),
        });
    });

    if (!chunks.length) return "";

    let out = chunks[0].value;

    for (let i = 1; i < chunks.length; i++) {
        const pair = `${chunks[i - 1].key}|${chunks[i].key}`;
        const sep = joins[rule][pair] ?? joins[rule].default;

        out += sep + chunks[i].value;
    }

    return out.replace(/\n+/g, "\n").trim();
}

export function convertCase(text, type = "snake") {
    // Ensure input is a string
    if (text === null || text === undefined) return "";

    text = String(text);

    const words = text
        .trim()
        // split camelCase / PascalCase
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        // replace separators (_ -) with spaces
        .replace(/[_-]+/g, " ")
        // remove special characters
        .replace(/[^\w\s]/g, "")
        // split into words
        .split(/\s+/)
        .filter(Boolean);

    switch (type) {
        case "snake":
            // helloWorld Example -> hello_world_example
            return words.map((w) => w.toLowerCase()).join("_");

        case "slug":
        case "kebab":
            // helloWorld Example -> hello-world-example
            return words.map((w) => w.toLowerCase()).join("-");

        case "camel":
            // hello_world example -> helloWorldExample
            return words
                .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
                .join("");

        case "pascal":
            // hello_world example -> HelloWorldExample
            return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join("");

        case "upper":
            // hello world example -> HELLO WORLD EXAMPLE
            return words.join(" ").toUpperCase();

        case "lower":
            // Hello WORLD Example -> hello world example
            return words.join(" ").toLowerCase();

        case "title":
            // hello_world example -> Hello World Example
            return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

        default:
            // fallback: return safe string version
            return String(text);
    }
}
